/**
 * Spec 10.2.1 — Trust place. Lists the workspace's ledgers; each opens its
 * cockpit. Empty/loading/error states designed.
 */
import { Link } from "react-router";
import { useLedgers } from "../../hooks/use-ledger-workbenches";

const PROFILE_BADGE: Record<string, string> = {
  trust_general: "trust general", controlled_money: "controlled money", statutory: "statutory", office: "office", custom: "custom",
};
const MODE_BADGE: Record<string, string> = { out_of_band: "badge--oob", confirm: "badge--confirm", none: "badge--none" };
const MODE_LABEL: Record<string, string> = { out_of_band: "out-of-band authorisation", confirm: "confirm authorisation", none: "direct post" };

export function TrustPage() {
  const q = useLedgers();
  const ledgers = q.data?.ledgers ?? [];

  return (
    <div className="screen">
      <div className="pagehead"><h1>Trust</h1><span className="sub">Trust accounting ledgers</span></div>

      {q.isLoading ? (
        <div className="stack">
          {[0, 1].map((i) => (
            <div key={i} className="surface"><div className="surface__bar"><div className="skeleton sk-line" style={{ width: 240 }} /></div></div>
          ))}
        </div>
      ) : q.isError ? (
        <div className="surface"><div className="surface__section"><div className="error-state">
          <svg className="mark" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.4}><path d="M12 9v4m0 4h.01M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0Z" /></svg>
          <h3>Couldn’t load ledgers</h3><p>The trust ledgers are unavailable right now. Retry to reload.</p>
          <button data-variant="primary" onClick={() => q.refetch()}>Retry</button>
        </div></div></div>
      ) : ledgers.length === 0 ? (
        <div className="surface"><div className="surface__body"><div className="empty-state">
          <svg className="mark" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.3}><path d="M2.5 5.5h11M4 5.5V12a1.5 1.5 0 001.5 1.5h5A1.5 1.5 0 0012 12V5.5M6 3h4l1 2.5H5z" /></svg>
          <h3>No ledgers yet</h3>
          <p>A trust ledger is created over MCP (or by an agent). Once one exists, its cockpit, accounts and reconciliation appear here.</p>
        </div></div></div>
      ) : (
        <div className="stack">
          {ledgers.map((l) => (
            // №14.1.1 — the row was a bar-only `.surface`; its `.surface__bar`
            // paints `--surface-3`, the same colour as the page ground, so the
            // whole card read as transparent. `.listrow` is the canonical raised
            // (`--surface-1`) link-row with a real border + accent hover.
            <Link key={l.id} to={`/app/trust/${l.id}`} className="listrow">
              <div className="ledger-identity">
                <span className="statutory">{l.name}</span>
                <span className="bank">{[l.bank_name, l.bank_bsb ? `BSB ${l.bank_bsb}` : null, l.bank_account_no, l.currency].filter(Boolean).join(" · ") || l.profile}</span>
              </div>
              <span className="spacer" />
              <span className="badge badge--finance">{PROFILE_BADGE[l.profile] ?? l.profile}</span>
              <span className={`badge ${MODE_BADGE[l.post_authorisation] ?? "badge--neutral"}`}>{MODE_LABEL[l.post_authorisation] ?? l.post_authorisation}</span>
              {l.pending_authorisations > 0 ? <span className="pill pill--pending"><span className="dot" />{l.pending_authorisations} awaiting</span> : null}
              <span className="muted tiny">{l.open_accounts} account{l.open_accounts === 1 ? "" : "s"}</span>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
